import * as angular from 'angular';
import * as d3 from 'd3';
import * as _ from 'lodash';
import * as moment from 'moment';

import { PropertyType, KeyValuePair, GuidObjectType, ComponentDefinition, BulkEditProperty, Rule } from '../../../models';
import { guidEmpty, MomentHelper } from '../../../common';
import { IBulkEditService } from '../../../services';

class WtBulkEditModal implements angular.IComponentController {
    allProperties: KeyValuePair<string, string>[];
    currentVariableType = guidEmpty;
    filteredElements: any[];
    filteredProperties: BulkEditProperty[];
    formCtrl: angular.IFormController;
    ignoreParents: boolean;
    isIdentifiersBulkEdit: Boolean;
    onEdit;
    onlyVisible: boolean = true;
    propertyData: { [index: string]: any; };
    propertyTypes: { [index: string]: number; };
    replace: boolean = false;
    rules: Rule[];
    variables: any[] = [];

    readonly modalId: string;
    readonly objectType: string;
    readonly properties: BulkEditProperty[];
    readonly visibleElements: any[];

    private _withVariableType: string[];

    static $inject = [
        '$scope',
        '$timeout',
        'bulkEditService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$timeout: angular.ITimeoutService,
        private _bulkEditService: IBulkEditService
        ) {
        this._withVariableType = [
            'UnitGuid',
            'VariableSubTypeGuid',
            'QualifierGuid',
            'SourceUnitGuid'
        ];
    }

    $onInit() {
        this.clear();
        const propertyTypes = {};
        const propertyData = {};
        this.properties.forEach(k => {
            propertyTypes[k.key] = k.type;
            propertyData[k.key] = k.extraData;
        });
        this.propertyTypes = propertyTypes;
        this.propertyData = propertyData;
        this.filterProperties();
        const self = this;

        const timer = this._$timeout(() => {
            angular.element(`#${this.modalId}`).on('hidden.bs.modal', () => self.clear());
            angular.element(`#${this.modalId}`).on('shown.bs.modal', () => {
                this.isIdentifiersBulkEdit = this.isBulkEditOfIdentifiers(this.visibleElements);
                this.updateFilteredElements(this.visibleElements);
                this._$scope.$digest();
            });
            this._$timeout.cancel(timer);
        });

        this._$scope.$watch(() => this.replace, newValue => {
            this.filterProperties();
        });

        if (this.visibleElements) {
            this.updateFilteredElements(this.visibleElements);
        }
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['visibleElements']) {
            this.updateFilteredElements(changesObj.visibleElements.currentValue);
        }
    }

    isBulkEditOfIdentifiers(elements: any[]): Boolean {
        const types = _.uniq(elements.map(e => e.type));
        if (types.length > 0) {
            return types.filter(t => t !== undefined).length > 0;
        } else {
            return false;
        }
    }

    updateFilteredElements(elements: any[]) {
        this.filteredElements = this.ignoreParents
            ? elements.filter(v => !v.children || v.children.length === 0 || _.every(v.children, c => elements.indexOf(c) < 0))
            : elements;
    }

    clear() {
        this.rules = [];
        this.filteredElements = [];
        this.onlyVisible = true;
        this.addRule();
    }

    addRule() {
        const items = {
            property: '',
            new: '',
            old: '',
            id: this.rules.length > 0 ? d3.max(this.rules, r => r.id) + 1 : 0,
            isMultiple: false
        };
        this.rules.push(items);
    }

    deleteRule(id: number) {
        _.remove(this.rules, r => r.id === id);
        this.variables[`variable${id}`] = undefined;
    }

    postBulkEdit() {
        const type = GuidObjectType[this.objectType];
        const guidList = this.filteredElements.map(e => e.guid);
        const rules = this.rules.filter(r => !r.isMultiple);

        // Filter to push multiple field as multiple rules
        this.rules.filter(r => r.isMultiple).forEach(r => {
            for (const key of Object.keys((r.new as {}))) {
                rules.push({
                    new: r.new[key],
                    property: key,
                } as Rule);
            }
        });

        // Filter to set frequency in good format
        rules.filter(r => this.propertyTypes[r.property] === PropertyType.Frequency)
            .forEach(l => {
                l.old = this.convert(l.old);
                l.new = this.convert(l.new);
            });

        return this._bulkEditService
            .bulkEdit(this.onlyVisible, this.replace, rules, guidList, type)
            .then((data) => {
                // call back our caller
                this.onEdit({ data });
            });
    }

    hasVariableType(property: string): boolean {
        return this._withVariableType.indexOf(property) >= 0;
    }

    select(rule: Rule) {
        const type = this.propertyTypes[rule.property];
        const property: BulkEditProperty = _.find(this.properties, p => p.type === type);

        rule.isMultiple = (type === PropertyType.VariablesAndUnit);
        rule['values'] = property.values;

        if (this.hasVariableType(rule.property)) {
            this.variables[`variable${rule.id}`] = this.currentVariableType;
        }

        if (type === PropertyType.Frequency) {
            rule.old = {} as any;
            rule.new = {} as any;
        } else {
            rule.old = '';
            rule.new = '';
        }
    }

    disabled(): boolean {
        return !this.rules.length || this.formCtrl.$invalid;
    }

    private filterProperties() {
        this.filteredProperties = this.properties.filter(p => this.replace ? !p.onlyNotMatching : !p.onlyMatching);
        this.rules.filter(r => !this.filteredProperties[r.property]).forEach(r => {
            r.new = '';
            r.old = '';
            r.property = '';
            r.isMultiple = false;
            delete r['values'];
        });
    }

    /**
     * convert into iso serialized span
     * @param freq
     */
    private convert(freq: any): string {
        const isNegative = freq.interval < 0;
        const interval = freq && freq.interval ? Math.abs(freq.interval) : 0;
        const duration = moment.duration(interval, MomentHelper.getPrefix(freq.intervalTimeUnit));
        const dayFormat = d3.format('0');
        const twoFormat = d3.format('02');
        const threeFormat = d3.format('03');
        const formatted = dayFormat(duration.days() + duration.months() * 30 + duration.years() * 365)
            + '.'
            + twoFormat(duration.hours())
            + ':'
            + twoFormat(duration.minutes())
            + ':'
            + twoFormat(duration.seconds())
            + '.'
            + threeFormat(duration.milliseconds());
        return (isNegative ? '-' : '') + formatted;
    }

    changeType(type) {
        this.currentVariableType = type;
    }
}

export const WtBulkEditModalDirective: ComponentDefinition = {
    componentId: 'wtBulkEditModal',
    // static moduleId : 'wtBulkEditModal';
    template: require('./bulkEditModal.component.html'),
    controller: WtBulkEditModal,
    controllerAs: 'vm',
    bindings: {
        modalId: '@',
        objectType: '@',
        visibleElements: '<',
        onEdit: '&',
        properties: '<'
    }
};